import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { Tag } from '../tag/tag.component';

interface LevelBadge {
  label: string;
  tooltip: string;
}

const LEVEL_PATTERN = /^L(\d+)$/;

const TOP_LEVEL_TOOLTIP = 'Top-level supplier — no parent company above it';

function levelTooltip(depth: number): string {
  if (depth <= 1) {
    return TOP_LEVEL_TOOLTIP;
  }

  if (depth === 2) {
    return 'Direct subsidiary of an L1 supplier';
  }

  return `Subsidiary of an L${depth - 1} supplier`;
}

const TCD_TOOLTIP = 'Not available for operational sourcing';
const ETCD_TOOLTIP = 'Available for operational sourcing';
const TCD_WITH_ETCD_TOOLTIP =
  'This supplier is TCD and cannot be used for sourcing. It is also classified as ETCD, meaning it may be used directly as an operational entity.';

@Component({
  selector: 'procure-supplier-level-badges',
  templateUrl: './supplier-level-badges.component.html',
  styleUrl: './supplier-level-badges.component.scss',
  imports: [Tag],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupplierLevelBadges {
  readonly levels = input<readonly string[] | null>(null);

  protected readonly badges = computed<LevelBadge[]>(() => {
    const levels = (this.levels() ?? []).map((level) => level.toUpperCase());
    const isTcd = levels.includes('TCD');
    const isEtcd = levels.includes('ETCD');

    if (isTcd || isEtcd) {
      const badges: LevelBadge[] = [];

      if (isTcd) {
        badges.push({
          label: 'TCD',
          tooltip: isEtcd ? TCD_WITH_ETCD_TOOLTIP : TCD_TOOLTIP,
        });
      }

      if (isEtcd) {
        badges.push({ label: 'ETCD', tooltip: ETCD_TOOLTIP });
      }

      return badges;
    }

    for (const entry of levels) {
      const depth = LEVEL_PATTERN.exec(entry)?.[1];

      if (depth) {
        return [{ label: entry, tooltip: levelTooltip(Number(depth)) }];
      }
    }

    return [];
  });
}
